import { addToast } from '@heroui/react';
import axios, { AxiosError, AxiosInstance } from 'axios';
import { signOut } from 'firebase/auth';
import { TriangleAlert } from 'lucide-react';
import { createElement } from 'react';

import { auth } from '../config/firebase';
import { i18n } from '../utils/i18n';
import { buildLocalePath } from '../utils/locale';
import { compactToastProps, TOAST_TIMEOUT } from '../utils/toastStyles';

const baseURL = import.meta.env.VITE_URL ? import.meta.env.VITE_URL : 'https://europe-west1-hotelspot-dev.cloudfunctions.net';

export const axiosInstance: AxiosInstance = axios.create({
  baseURL,
  timeout: 60000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// Dołącza Firebase ID token (jeśli brak ręcznie ustawionego) — backend v3 autoryzuje.
axiosInstance.interceptors.request.use(async (config) => {
  if (!config.headers.Authorization) {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

let isAILimitToastVisible = false;

export const isAILimitToastActive = (): boolean => isAILimitToastVisible;

const warningToastProps = compactToastProps('warning');

let isHandling401 = false;

// Response interceptor dla błędów AI Token Limit
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // 401 = token wygasł/nieważny → wyloguj i przekieruj na /login (raz).
    if (error.response?.status === 401 && !isHandling401 && !window.location.pathname.includes('/login')) {
      isHandling401 = true;
      addToast({
        ...warningToastProps,
        title: i18n.t('auth.sessionExpired.title', 'Sesja wygasła'),
        description: i18n.t('auth.sessionExpired.description', 'Zaloguj się ponownie.'),
        timeout: TOAST_TIMEOUT.long,
        icon: createElement(TriangleAlert, { size: 16, strokeWidth: 2 }),
      });
      signOut(auth).catch(() => {}).finally(() => {
        const locale = i18n.language || 'pl';
        window.history.pushState({}, '', buildLocalePath('/login', locale));
        window.dispatchEvent(new PopStateEvent('popstate'));
        isHandling401 = false;
      });
      return Promise.reject(error);
    }

    if (error.response?.status === 429) {
      const data = error.response.data as any;

      if (data?.error === 'AI token limit exceeded') {
        isAILimitToastVisible = true;

        const titleKey = data?.titleKey || 'aiUsage.limitExceeded.title';
        const descKey = data?.descriptionKey || 'aiUsage.limitExceeded.description';

        // Bez przycisku „zmień plan" — ekran zmiany planu został usunięty,
        // więc zostaje sama informacja o wyczerpanym limicie.
        const descriptionWithButton = createElement(
          'div',
          null,
          createElement('span', null, i18n.t(descKey)),
        );

        addToast({
          ...warningToastProps,
          title: i18n.t(titleKey),
          // @ts-expect-error HeroUI types description as string but renders ReactNode fine
          description: descriptionWithButton,
          timeout: 10000,
          icon: createElement(TriangleAlert, { size: 16, strokeWidth: 2 }),
        });

        const handled = new Error('AI_LIMIT_HANDLED') as any;
        handled._aiLimitHandled = true;
        return Promise.reject(handled);
      }
    }

    return Promise.reject(error);
  },
);
